import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
// Mock pattern data - replace with actual pattern generation logic
const PATTERN_DATA = {
  corePattern: 'You tend to move by taking responsibility early and absorbing uncertainty yourself.',
  defaultMove: ['You step in before being asked', 'You take ownership of ambiguous situations', 'You absorb risk rather than distribute it'],
  optimizesFor: ['Speed and decisiveness', 'Clarity in unclear situations', 'Accountability and follow-through'],
  costs: ['Accumulating load faster than you shed it', 'Becoming the bottleneck', 'Creating friction with people who need more process'],
  loop: ['Under pressure, you take on more to stabilize the situation.', 'This increases your load and visibility.', 'People start depending on you to resolve ambiguity.', 'Which pushes you to take on even more.']
};
export function WorkMirrorResult() {
  const navigate = useNavigate();
  const handleContinue = () => {
    navigate('/work-mirror/app');
  };
  const handleBack = () => {
    navigate('/work-mirror');
  };
  const handleShare = () => {
    // Share functionality
    const reportId = `shared-${Date.now()}`;
    const url = `${window.location.origin}/work-mirror/shared/${reportId}`;
    navigator.clipboard.writeText(url);
  };
  return <>
      <Helmet data-id="element-4120">
        <title data-id="element-4121">Baseline Pattern | Work Mirror</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-4122" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-4123">
        {/* Top Bar */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A] border-b border-[#2B2B2B]" data-id="element-4124">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-4125">
            <button onClick={handleBack} className="text-sm text-gray-500 hover:text-gray-400 transition-colors" data-id="element-4126">
              Back
            </button>

            <div className="flex items-center gap-6" data-id="element-4127">
              <button onClick={handleShare} className="text-sm text-gray-500 hover:text-gray-400 transition-colors" data-id="element-4128">
                Share
              </button>
            </div>
          </div>
        </header>

        <main className="pt-24 pb-16 px-6" data-id="element-4129">
          <div className="max-w-3xl mx-auto space-y-16" data-id="element-4130">
            {/* Section 1 — Title */}
            <section className="space-y-6 pt-12" data-id="element-4131">
              <p className="text-xs font-mono text-gray-600 uppercase tracking-widest" data-id="element-4132">
                Baseline pattern
              </p>

              <h1 className="text-2xl md:text-3xl text-[#F5F5F5] leading-relaxed font-light" data-id="element-4133">
                {PATTERN_DATA.corePattern}
              </h1>
            </section>

            {/* Section 2 — The Structure (3 Blocks) */}
            <section className="space-y-12 pt-8 border-t border-[#2B2B2B]" data-id="element-4134">
              <h2 className="text-base font-medium text-[#F5F5F5]" data-id="element-4135">
                Structure
              </h2>

              <div className="space-y-12" data-id="element-4136">
                {/* 1. Default Move */}
                <div className="space-y-4" data-id="element-4137">
                  <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-4138">
                    Default move
                  </p>
                  <div className="space-y-3 text-base text-gray-400 leading-relaxed" data-id="element-4139">
                    {PATTERN_DATA.defaultMove.map((line, i) => <p key={i} data-id="element-4140">• {line}</p>)}
                  </div>
                </div>

                {/* 2. What It Optimizes For */}
                <div className="space-y-4" data-id="element-4141">
                  <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-4142">
                    What it optimizes for
                  </p>
                  <div className="space-y-3 text-base text-gray-400 leading-relaxed" data-id="element-4143">
                    {PATTERN_DATA.optimizesFor.map((line, i) => <p key={i} data-id="element-4144">• {line}</p>)}
                  </div>
                </div>

                {/* 3. What It Consistently Costs */}
                <div className="space-y-4" data-id="element-4145">
                  <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-4146">
                    What it consistently costs
                  </p>
                  <div className="space-y-3 text-base text-gray-400 leading-relaxed" data-id="element-4147">
                    {PATTERN_DATA.costs.map((line, i) => <p key={i} data-id="element-4148">• {line}</p>)}
                  </div>
                </div>
              </div>
            </section>

            {/* Section 3 — The Loop */}
            <section className="space-y-8 pt-8 border-t border-[#2B2B2B]" data-id="element-4149">
              <h2 className="text-base font-medium text-[#F5F5F5]" data-id="element-4150">The loop</h2>

              <div className="space-y-6" data-id="element-4151">
                {PATTERN_DATA.loop.map((step, i) => <div key={i} className="flex gap-4" data-id="element-4152">
                    <span className="text-sm font-mono text-gray-600 flex-shrink-0" data-id="element-4153">
                      {i + 1}.
                    </span>
                    <p className="text-base text-gray-400 leading-relaxed" data-id="element-4154">
                      {step}
                    </p>
                  </div>)}
              </div>
            </section>

            {/* Section 4 — What This Is */}
            <section className="pt-8 border-t border-[#2B2B2B]" data-id="element-4155">
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-4156">
                This is not advice.
                <br data-id="element-4157" />
                This is a structural baseline the mirror will use to annotate
                your daily entries.
              </p>
            </section>

            {/* Section 5 — Continue */}
            <section className="pt-8" data-id="element-4158">
              <button onClick={handleContinue} className="px-10 py-4 bg-[#F5F5F5] text-[#0A0A0A] font-medium text-sm hover:bg-gray-200 transition-colors" data-id="element-4159">
                Go to Work Mirror
              </button>
            </section>
          </div>
        </main>
      </div>
    </>;
}